import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Subject } from 'rxjs';

import { ConfigType, ViewSettingsRepositoryBase } from '@skylab-shared/business';
import { ConsoleLogger } from '@skylab-shared/core';

import { ViewSettingsRepository } from '@skylab-angular-shared/business';

export type ThemeType = 'dark' | 'light';
export type ThemeSwitcherType = 'system' | ThemeType;

@Injectable({
    providedIn: 'root'
})
export class ThemeSwitcherService {
    private ATTR_PROVIDER = 'data-theme' as const;
    private STORAGE_THEME_KEY: ConfigType = 'app_theme_type' as const;
    private themeQueryList!: MediaQueryList;
    private renderer!: Renderer2;
    private isSystemDark!: boolean;

    public themeChange$ = new Subject<ThemeSwitcherType>();

    private readonly logger = new ConsoleLogger(this.constructor.name);
    private storedTheme: ThemeType | null = null;

    public constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(ViewSettingsRepository) private viewSettingsRepository: ViewSettingsRepositoryBase,
        private rendererFactory: RendererFactory2
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
        this.themeQueryList = matchMedia('(prefers-color-scheme: dark)');
    }

    private loaded = false;
    public async loadAsync(): Promise<void> {
        if (this.loaded) return;

        try {
            const theme = await this.loadConfigThemeAsync();
            this.storedTheme = theme;
        } catch (err) {
            this.logger.warn(err);
        } finally {
            this.themeChange$.next(this.currentSwitcherType);
            this.renderTheme();
            this.themeQueryList.addEventListener?.('change', () => {
                this.renderTheme();
            });
            this.loaded = true;
        }
    }

    private renderTheme(): void {
        this.renderer.setAttribute(this.document.body, this.ATTR_PROVIDER, this.currentTheme);
    }

    public get currentTheme(): ThemeSwitcherType {
        return this.storedTheme ?? this.systemTheme;
    }

    public get currentSwitcherType(): ThemeSwitcherType {
        return this.storedTheme ? this.currentTheme : 'system';
    }

    public get systemTheme(): ThemeType {
        this.isSystemDark = this.themeQueryList.matches;
        return this.isSystemDark ? 'dark' : 'light';
    }

    public async saveThemeAsync(theme: ThemeType | null): Promise<void> {
        this.viewSettingsRepository.saveViewSettingsAsync(this.STORAGE_THEME_KEY, theme);
    }

    public async loadConfigThemeAsync(): Promise<ThemeType | null> {
        return await this.viewSettingsRepository.getViewSettingsAsync(this.STORAGE_THEME_KEY);
    }

    public setTheme(theme: ThemeSwitcherType): void {
        this.themeChange$.next(theme);
        this.storedTheme = theme !== 'system' ? theme : null;
        this.renderTheme();
        this.saveThemeAsync(this.storedTheme);
    }
}
